// The manifest behind every create flow (ui-spec §3.5): YAML is the source of truth; the CLI and Python tabs render
// the same object, scoped to the page's org and project.
import { parse, stringify } from "yaml";
import { cliCommand, type CliScope } from "./cli";

export type Manifest = Record<string, unknown> & { apiVersion?: string; kind?: string; metadata?: Record<string, unknown> };

export function toYaml(m: Manifest): string {
  return stringify(m, { lineWidth: 0 });
}

/** Parses edited YAML; the error is a plain sentence for the field's inline message. */
export function fromYaml(text: string): { manifest: Manifest } | { error: string } {
  try {
    const v = parse(text) as unknown;
    if (!v || typeof v !== "object" || Array.isArray(v)) return { error: "The manifest must be one YAML object." };
    const m = v as Manifest;
    if (typeof m.kind !== "string" || typeof m.apiVersion !== "string") return { error: "The manifest needs apiVersion and kind." };
    return { manifest: m };
  } catch (e) {
    return { error: e instanceof Error ? e.message.split("\n")[0]! : "Not valid YAML." };
  }
}

/** `nodus apply -f - -p <project> --org <org>` with the manifest on stdin. */
export function applyCommand(m: Manifest, scope: CliScope, omitScope = false): string {
  return `${cliCommand(["apply", "-f", "-"], scope, { omitScope })} <<'EOF'\n${toYaml(m)}EOF`;
}

/** The Python SDK equivalent: estimate (dry-run) then create bound to it (resources.md §10.3). */
export function pythonSnippet(m: Manifest): string {
  const body = JSON.stringify(m, null, 4).split("\n")
    .map((line) => `    ${JSON.stringify(`${line}\n`)}`).join("\n");
  return `import json\nimport nodus\n\nmanifest = json.loads(\n${body}\n)\n\nestimate, etag = nodus.api.estimate(manifest)\nprint(estimate)\nnodus.api.create(manifest, if_match=etag)\n`;
}

/**
 * A manifest for Duplicate (`?from=kind/name`): the spec and user labels of an existing object, without server-set
 * metadata or status, under a new name.
 */
export function duplicateOf(obj: Manifest, newName: string): Manifest {
  const meta = (obj.metadata ?? {}) as { labels?: Record<string, string>; annotations?: Record<string, string>; namespace?: string };
  const labels = Object.fromEntries(Object.entries(meta.labels ?? {}).filter(([k]) => !k.startsWith("nodus.dev/")));
  return {
    apiVersion: obj.apiVersion,
    kind: obj.kind,
    metadata: { name: newName, ...(meta.namespace ? { namespace: meta.namespace } : {}), ...(Object.keys(labels).length ? { labels } : {}) },
    ...(obj.spec ? { spec: structuredClone(obj.spec) } : {}),
  };
}

/** A DNS-1123 name with a short random suffix, prefilled in create forms (ui-spec §3.5 item 6). */
export function generatedName(prefix: string, random: () => number = Math.random): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  let suffix = "";
  for (let i = 0; i < 5; i++) suffix += alphabet[Math.floor(random() * alphabet.length)];
  const base = prefix.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 57) || "run";
  return `${base}-${suffix}`;
}

export function nameProblem(name: string): string | undefined {
  if (!name) return "Enter a name.";
  if (name.length > 63) return "Use at most 63 characters.";
  if (!/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name)) return "Use lowercase letters, digits and hyphens; start and end with a letter or digit.";
  return undefined;
}
